/**
 * Reset Password Page
 * ===================
 * Password reset confirmation page with token.
 *
 * UI mirrors the Login page: the same soft light backdrop (grid + brand blobs),
 * elevated white card, AuthLogo / BrandWordmark logo treatment, "Show/Hide"
 * password toggles, and accent submit button — so the auth flow feels like one
 * continuous experience.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams, Link } from 'react-router-dom';
import authAPI from '../api/auth';
import { useOrgBranding } from '../hooks/useOrgBranding';
import { useDocumentTitle } from '../hooks';
import { DEFAULT_BRAND_NAME } from '../utils/constants';
import AuthLogo from '../components/auth/AuthLogo';
import BrandWordmark from '../components/auth/BrandWordmark';
import { Button, Alert } from '../components/ui';
import { calculatePasswordStrength } from '../components/users/PasswordStrengthIndicator';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { orgSlug } = useParams();
  const token = searchParams.get('token');
  const { branding: orgBranding, resolved: brandingResolved } = useOrgBranding(orgSlug);
  const brandName = orgBranding?.name || DEFAULT_BRAND_NAME;
  useDocumentTitle(`${brandName} Admin Portal`);

  // Show the org's own logo/name when it has branding; otherwise fall back to
  // the default product wordmark so the page always carries a logo (matches Login).
  const hasOrgBranding = Boolean(orgBranding?.logo_url || orgBranding?.name);

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const loginHref = orgSlug ? `/${orgSlug}/login` : '/login';
  const forgotHref = orgSlug ? `/${orgSlug}/forgot-password` : '/forgot-password';

  // Redirect to login if no token provided
  useEffect(() => {
    if (!token) {
      navigate(loginHref, { replace: true });
    }
  }, [token, navigate, loginHref]);

  /**
   * Validate form fields
   */
  const validateForm = () => {
    const newErrors = {};

    // Password validation
    if (!formData.newPassword) {
      newErrors.newPassword = 'Password is required';
    } else {
      const { passed } = calculatePasswordStrength(formData.newPassword);
      if (passed.length < 5) {
        newErrors.newPassword = 'Password does not meet all requirements';
      }
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle input change
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await authAPI.resetPassword(token, formData.newPassword, formData.confirmPassword);
      setSuccess(true);

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate(loginHref, {
          state: { message: 'Password reset successful. Please login with your new password.' }
        });
      }, 2000);
    } catch (error) {
      console.error('Reset password error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to reset password. Please try again.';

      // If token is invalid/expired, offer to request new reset
      if (errorMessage.includes('Invalid') || errorMessage.includes('expired')) {
        setApiError(
          <div>
            {errorMessage}
            <Link
              to={forgotHref}
              className="block mt-2 text-sm font-medium text-primary hover:text-primary-600"
            >
              Request a new reset link
            </Link>
          </div>
        );
      } else {
        setApiError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Shared soft brand backdrop — identical to the Login page.
  const backdrop = (
    <div className="pointer-events-none absolute inset-0" aria-hidden="true">
      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,125,136,0.055)_1px,transparent_1px),linear-gradient(90deg,rgba(0,125,136,0.055)_1px,transparent_1px)] bg-[size:28px_28px]" />
      <div className="absolute -top-40 left-1/4 h-[32rem] w-[42rem] -translate-x-1/2 rounded-full bg-primary/20 blur-[130px]" />
      <div className="absolute -bottom-40 right-0 h-[28rem] w-[32rem] translate-x-1/4 rounded-full bg-accent/20 blur-[120px]" />
      <div className="absolute top-1/2 left-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/10 blur-[110px]" />
    </div>
  );

  // Logo block — matches Login: org logo when branded, product wordmark otherwise.
  const logo = (
    <div className="mb-8 flex justify-center">
      {!brandingResolved || hasOrgBranding ? (
        <AuthLogo branding={orgBranding} resolved={brandingResolved} variant="light" />
      ) : (
        <Link to="/" aria-label={DEFAULT_BRAND_NAME}>
          <BrandWordmark tone="dark" size="lg" />
        </Link>
      )}
    </div>
  );

  const footer = (
    <p className="mt-8 text-center text-sm text-neutral-400">
      {brandName} &copy; {new Date().getFullYear()} All rights reserved.
    </p>
  );

  // Show success state
  if (success) {
    return (
      <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-neutral-50 px-4 py-12">
        {backdrop}

        <div className="relative w-full max-w-md animate-slide-up">
          <div className="overflow-hidden rounded-2xl bg-white shadow-strong ring-1 ring-neutral-900/5">
            <div className="px-8 py-10 sm:px-10">
              {logo}

              <div className="text-center">
                <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                  <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h1 className="text-center text-2xl font-bold text-neutral-900">
                  Password Reset Successful!
                </h1>
                <p className="mt-2 text-neutral-500">
                  Your password has been successfully reset.
                </p>
                <p className="mt-2 text-sm text-neutral-400">
                  Redirecting to login...
                </p>
              </div>
            </div>
          </div>

          {footer}
        </div>
      </div>
    );
  }

  // Calculate password strength for display
  const passwordStrength = calculatePasswordStrength(formData.newPassword);
  const strengthColors = {
    none: { bar: 'bg-neutral-400', text: 'text-neutral-600' },
    weak: { bar: 'bg-red-500', text: 'text-red-600' },
    medium: { bar: 'bg-yellow-500', text: 'text-yellow-600' },
    strong: { bar: 'bg-green-500', text: 'text-green-600' },
  };
  const colors = strengthColors[passwordStrength.level] || strengthColors.none;

  // Show form
  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-neutral-50 px-4 py-12">
      {backdrop}

      <div className="relative w-full max-w-md animate-slide-up">
        <div className="overflow-hidden rounded-2xl bg-white shadow-strong ring-1 ring-neutral-900/5">
          <div className="px-8 py-10 sm:px-10">
            {logo}

            {/* Heading */}
            <h1 className="text-center text-2xl font-bold text-neutral-900">
              Reset your password
            </h1>
            <p className="mt-2 mb-8 text-center text-neutral-500">
              Enter your new password below to regain access to {brandName}.
            </p>

            {/* Error Alert */}
            {apiError && (
              <div className="mb-6">
                <Alert variant="error" title="Error">
                  {apiError}
                </Alert>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* New Password */}
              <div>
                <label className="label label-required" htmlFor="newPassword">New Password</label>
                <div className="relative">
                  <input
                    id="newPassword"
                    name="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={formData.newPassword}
                    onChange={handleChange}
                    placeholder="Enter a strong password"
                    disabled={loading}
                    autoFocus
                    className={`input ${errors.newPassword ? 'input-error' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold uppercase tracking-wide text-primary-600 hover:text-primary-700"
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                {errors.newPassword && (
                  <p className="mt-2 text-sm text-red-600">{errors.newPassword}</p>
                )}

                {/* Password Strength Indicator */}
                {formData.newPassword && (
                  <div className="mt-2">
                    <div className="mb-1 flex items-center justify-between">
                      <span className="text-xs text-neutral-600">Password strength:</span>
                      <span className={`text-xs font-medium ${colors.text}`}>
                        {passwordStrength.level === 'none' ? 'Too weak' :
                         passwordStrength.level === 'weak' ? 'Weak' :
                         passwordStrength.level === 'medium' ? 'Medium' : 'Strong'}
                      </span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-neutral-200">
                      <div
                        className={`h-2 rounded-full transition-all ${colors.bar}`}
                        style={{ width: `${passwordStrength.strength}%` }}
                      />
                    </div>
                  </div>
                )}

                <p className="mt-2 text-xs text-neutral-500">
                  Password must be at least 12 characters with uppercase, lowercase, number, and special character.
                </p>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="label label-required" htmlFor="confirmPassword">Confirm Password</label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Re-enter your password"
                    disabled={loading}
                    className={`input ${errors.confirmPassword ? 'input-error' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold uppercase tracking-wide text-primary-600 hover:text-primary-700"
                  >
                    {showConfirmPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-2 text-sm text-red-600">{errors.confirmPassword}</p>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-2">
                <Button
                  type="submit"
                  variant="accent"
                  size="lg"
                  loading={loading}
                  disabled={loading}
                  className="w-full"
                >
                  {loading ? 'Resetting Password...' : 'RESET PASSWORD'}
                </Button>
              </div>

              {/* Back to Login */}
              <div className="text-center">
                <Link
                  to={loginHref}
                  className="text-sm font-medium text-primary hover:text-primary-600"
                >
                  Back to login
                </Link>
              </div>
            </form>
          </div>
        </div>

        {footer}
      </div>
    </div>
  );
};

export default ResetPassword;
